package guide.kuki.app.engine

import kotlin.math.floor

/**
 * Generates the ordered list of ring/block address names from the
 * Cook Islands Maori syllable system.
 *
 * Vowels: a, e, i, o, u
 * Consonants: ng, k, m, n, p, r, t, v
 *
 * Layout: all variant-1 names first (tai suffix) — vowel-only then each
 * consonant-vowel combination — then all variant-2 names (rua suffix) in
 * the same order. Total (5 + 8×5) × 2 = 90 names per slice.
 * Custom names from [names] override the generated defaults.
 */
fun generateAddresses(names: Map<String, String>): List<String> {
    val letters = mutableListOf<String>()
    for (variant in 1..2) {
        val suffix = if (variant == 1) "tai" else "rua"
        for (v in NAME_VOWELS) {
            val key = "$v$variant"
            val name = names[key]
            letters.add(if (!name.isNullOrEmpty()) name else "${v.uppercase()}$suffix")
        }
        for (c in NAME_CONSONANTS) {
            val prefix = if (c == "ng") "Ng" else c.uppercase()
            for (v in NAME_VOWELS) {
                val key = "$c$v$variant"
                val name = names[key]
                letters.add(if (!name.isNullOrEmpty()) name else "$prefix$v$suffix")
            }
        }
    }
    return letters
}

private val NAME_VOWELS = listOf("a", "e", "i", "o", "u")
private val NAME_CONSONANTS = listOf("ng", "k", "m", "n", "p", "r", "t", "v")
private const val VOWEL_ONLY_PER_VARIANT = 5         // 5 vowels
private const val CONSONANT_VOWEL_PER_VARIANT = 40   // 8 consonants × 5 vowels
private const val BLOCKS_PER_VARIANT = VOWEL_ONLY_PER_VARIANT + CONSONANT_VOWEL_PER_VARIANT // 45
private const val MAX_SUB_LETTERS = 3
private val NAME_CODE_REGEX = Regex("^(ng|[kmnprtv])?([aeiou])([12])")
private val SEARCH_CODE_REGEX =
    Regex("^(\\d+)((?:ng|[kmnprtv])?[aeiou])([12])([a-z]*)$", RegexOption.IGNORE_CASE)

/**
 * Core radial map engine. Manages the grid of radial lines and provides
 * block lookup by geographic coordinates or by address string.
 *
 * @param radials Raw radial coordinate data — list of radial lines,
 *   each being a list of [lat, lon] pairs from center to reef.
 * @param sections Number of block subdivisions per radial section.
 */
class RadialMap(
    radials: List<List<DoubleArray>>,
    private val divs: List<Int>,
) {
    val blocksPerRadial: Int = divs.sum()
    val totalRadials: Int
    val slicesPerHour: Int
    /** Number of ring-boundary points along each radial (from the JSON). */
    val pointsPerRadial: Int
    private val lastIdx: Int

    private val radials: List<List<Coords>>

    private var block: Block = Block()

    private val reNumbers = Regex("^[0-9]+")
    private val reSubletters = Regex("[a-z]+$")

    var addresses: List<String> = generateAddresses(emptyMap())
        private set

    init {
        totalRadials = radials.size
        slicesPerHour = totalRadials / 12
        pointsPerRadial = radials[0].size
        lastIdx = radials[0].size - 1

        this.radials = radials.map { radial ->
            radial.map { pt -> Coords(pt[0], pt[1]) }
        }
    }

    fun loadAddresses(names: Map<String, String>) {
        addresses = generateAddresses(names)
    }

    fun getAddress(blk: Block = block): String {
        if (!blk.isValid) return ""
        val s = blk.slice ?: return ""
        val sliceIdx = s.idx + 1
        val hour = sliceIdx / slicesPerHour
        val minute = sliceIdx - hour * slicesPerHour
        val minuteStr = if (hour > 0 && minute < 10) "0$minute" else "$minute"
        return if (hour == 0) {
            "$minuteStr ${addresses[blk.idx]}"
        } else {
            "$hour$minuteStr ${addresses[blk.idx]}"
        }
    }

    fun searchAddress(searchText: String): List<String> {
        val results = mutableListOf<String>()
        val trimmed = searchText.trim()
        if (trimmed.isEmpty()) return results

        // 1. Complete address code (e.g. "1008na1", "1008na1mc") → single
        // resolved suggestion. The block name lookup uses the raw addresses
        // table so we don't have to mutate engine state.
        SEARCH_CODE_REGEX.matchEntire(trimmed)?.let { codeMatch ->
            val numberStr = codeMatch.groupValues[1]
            val ringPart = codeMatch.groupValues[2].lowercase()
            val variant = codeMatch.groupValues[3].toInt()
            val subLetters = codeMatch.groupValues[4].lowercase()
            val blockIdx = blockIdxForRingCode(ringPart, variant)
            if (blockIdx in addresses.indices) {
                results.add("$numberStr$subLetters ${addresses[blockIdx]} Blk")
                return results
            }
        }

        // 2. Normalise. First insert a space between the leading sector token
        // (digits + optional sub-letters) and an uppercase name letter, so
        // "1008Naea" parses the same as "1008 Naea". Then, if the input is
        // still single-token and lowercase, split between the digits and the
        // name (so "1008na" parses as "1008 na").
        val spacedUpper = trimmed.replace(Regex("^(\\d+[a-z]*)([A-Z])"), "$1 $2")
        val spaced = if (spacedUpper.contains(' ')) {
            spacedUpper
        } else {
            spacedUpper.replace(Regex("^(\\d+)([a-zA-Z].*)$"), "$1 $2")
        }

        val split = spaced.replace(Regex("  +"), " ").split(" ")
        if (split.size < 2) return results

        val number = split[0].lowercase()
        val name = split[1].lowercase()
        if (name.isEmpty()) return results

        // The bare letter "n" means the standalone consonant — exclude the
        // digraph "ng" matches. All other 1-letter prefixes match anything
        // starting with that letter.
        val letters: String
        val excludeNg: Boolean
        when {
            name == "n" -> {
                letters = "N"; excludeNg = true
            }
            name.length == 1 -> {
                letters = name[0].uppercase(); excludeNg = false
            }
            name.length == 2 -> {
                letters = name[0].uppercase() + name[1]; excludeNg = false
            }
            name[0] == 'n' && name[1] == 'g' -> {
                letters = name[0].uppercase() + name[1] + name[2]; excludeNg = false
            }
            else -> {
                letters = name[0].uppercase() + name[1]; excludeNg = false
            }
        }

        if (letters.isEmpty()) return results

        for (address in addresses) {
            if (!address.startsWith(letters)) continue
            if (excludeNg && address.startsWith("Ng")) continue
            results.add("$number $address Blk")
        }

        return results
    }

    private fun blockIdxForRingCode(ringPart: String, variant: Int): Int {
        if (variant !in 1..2) return -1
        val variantOffset = (variant - 1) * BLOCKS_PER_VARIANT
        return if (ringPart.length == 1) {
            val vowelIdx = NAME_VOWELS.indexOf(ringPart)
            if (vowelIdx < 0) -1 else variantOffset + vowelIdx
        } else {
            val vowel = ringPart.takeLast(1)
            val consonant = ringPart.dropLast(1)
            val vowelIdx = NAME_VOWELS.indexOf(vowel)
            val consonantIdx = NAME_CONSONANTS.indexOf(consonant)
            if (vowelIdx < 0 || consonantIdx < 0) -1
            else variantOffset + VOWEL_ONLY_PER_VARIANT + consonantIdx * NAME_VOWELS.size + vowelIdx
        }
    }

    fun getSlice(idx: Int): Slice {
        return Slice(idx, getRadial(idx), getRadial(idx + 1), divs)
    }

    fun getRadial(idx: Int): List<Coords> {
        return when {
            idx >= totalRadials -> radials[idx - totalRadials]
            idx < 0 -> radials[idx + totalRadials]
            else -> radials[idx]
        }
    }

    fun findBlock(latLng: LatLng): Block {
        if (block.isValid) {
            if (block.containsLatLon(latLng)) {
                block.updateLatLng(latLng)
                return block
            }
            val currentSlice = block.slice
            if (currentSlice != null && currentSlice.containsLatLon(latLng)) {
                block = currentSlice.findBlock(latLng, blocksPerRadial)
            } else {
                val slice = findSlice(latLng)
                block = if (slice.isValid) {
                    slice.findBlock(latLng, blocksPerRadial)
                } else {
                    Block()
                }
            }
        } else {
            val slice = findSlice(latLng)
            block = if (slice.isValid) {
                slice.findBlock(latLng, blocksPerRadial)
            } else {
                Block()
            }
        }
        block.updateLatLng(latLng)
        return block
    }

    fun findBlockByAddress(address: String): Block? {
        val split = address.replace(Regex("  +"), " ").split(" ")
        if (split.size < 2) return null

        val matchNumbers = reNumbers.find(split[0]) ?: return null
        var number = matchNumbers.value.toInt() - 1
        if (number == 1200) number = 0
        val hour = number / 100
        val sliceIdx = hour * slicesPerHour + (number - hour * 100)

        val name = split[1].lowercase()
        if (name.isEmpty()) return null

        var blockIdx = -1
        for (i in addresses.indices) {
            if (addresses[i].lowercase().startsWith(name)) {
                blockIdx = i
                break
            }
        }
        if (blockIdx == -1) return null

        val slice = Slice(sliceIdx, getRadial(sliceIdx), getRadial(sliceIdx + 1), divs)
        block = slice.getBlock(blockIdx)

        val matchSubletters = reSubletters.find(split[0])
        if (matchSubletters != null) {
            applySubLetters(block, matchSubletters.value.toList())
        } else {
            block.resolveLatLng()
            block.clearSubBlock()
        }

        return block
    }

    /**
     * Walks [letters] as nested sub-block selectors (one letter per level),
     * stopping at MAX_SUB_LETTERS. Sets the parent block's latLng to the
     * deepest resolved sub-block's centre.
     */
    private fun applySubLetters(parent: Block, letters: List<Char>) {
        if (letters.isEmpty()) return
        val capped = letters.take(MAX_SUB_LETTERS)
        var cursor: Block = parent
        var deepest: Block = parent
        for (letter in capped) {
            // Stop at the first unknown/out-of-grid letter so we don't
            // silently extrapolate past the parent's geometry. The
            // resolved location is the deepest valid sub we found.
            val next = cursor.getSubBlockByLetter(letter) ?: break
            deepest = next
            cursor = next
        }
        parent.updateLatLng(deepest.resolveLatLng())
    }

    /**
     * Resolves a compact code like "1008ka1m" into a block.
     *
     * Format:
     *   <number><name-code><sub-letters>
     * Where:
     *   number — 1..1200, the slice index + 1
     *   name-code — `<consonant?><vowel><variant>`, e.g. "a1", "ka1", "nga2"
     *     consonant: ng, k, m, n, p, r, t, v (or none)
     *     vowel: a, e, i, o, u
     *     variant: 1 or 2
     *   sub-letters — 0..3 letters identifying nested sub-blocks
     *
     * Returns null if the code is malformed or out of range.
     */
    fun findBlockByCode(code: String): Block? {
        val rawNumberMatch = reNumbers.find(code) ?: return null
        var number = rawNumberMatch.value.toInt() - 1
        if (number < 0 || number >= totalRadials) return null
        if (number == 1200) number = 0
        val hour = number / 100
        val sliceIdx = hour * slicesPerHour + (number - hour * 100)

        val rest = code.substring(rawNumberMatch.value.length).lowercase()
        val nameMatch = NAME_CODE_REGEX.find(rest) ?: return null
        if (nameMatch.range.first != 0) return null

        val consonant = nameMatch.groupValues[1]
        val vowel = nameMatch.groupValues[2]
        val variant = nameMatch.groupValues[3].toInt()

        val vowelIdx = NAME_VOWELS.indexOf(vowel)
        if (vowelIdx < 0) return null
        val variantOffset = (variant - 1) * BLOCKS_PER_VARIANT
        val blockIdx = if (consonant.isEmpty()) {
            variantOffset + vowelIdx
        } else {
            val consonantIdx = NAME_CONSONANTS.indexOf(consonant)
            if (consonantIdx < 0) return null
            variantOffset + VOWEL_ONLY_PER_VARIANT + consonantIdx * NAME_VOWELS.size + vowelIdx
        }
        if (blockIdx >= blocksPerRadial) return null

        val slice = Slice(sliceIdx, getRadial(sliceIdx), getRadial(sliceIdx + 1), divs)
        block = slice.getBlock(blockIdx)

        val subLetters = rest.substring(nameMatch.value.length).toList()
        if (subLetters.isNotEmpty()) {
            applySubLetters(block, subLetters)
        } else {
            block.resolveLatLng()
            block.clearSubBlock()
        }
        return block
    }

    /**
     * Generates a compact code (the inverse of [findBlockByCode]) for [block].
     * If a sub-block has been resolved, its letter(s) are appended.
     */
    fun getBlockCode(block: Block): String {
        if (!block.isValid) return ""
        val s = block.slice ?: return ""
        val numberStr = sliceCodeNumber(s.idx)
        val nameCode = blockNameCode(block.idx) ?: return ""
        val sub = block.resolveSubBlock()
        val subLetters = if (sub.isValid) sub.subLetters else ""
        return "$numberStr$nameCode$subLetters"
    }

    private fun sliceCodeNumber(sliceIdx: Int): String {
        val n = sliceIdx + 1
        val hour = n / slicesPerHour
        val minute = n - hour * slicesPerHour
        return if (hour == 0) "$minute" else {
            val minStr = if (minute < 10) "0$minute" else "$minute"
            "$hour$minStr"
        }
    }

    private fun blockNameCode(blockIdx: Int): String? {
        val variant = blockIdx / BLOCKS_PER_VARIANT + 1
        if (variant > 2) return null
        val rel = blockIdx % BLOCKS_PER_VARIANT
        if (rel < VOWEL_ONLY_PER_VARIANT) {
            return "${NAME_VOWELS[rel]}$variant"
        }
        val consonantRel = rel - VOWEL_ONLY_PER_VARIANT
        val consonantIdx = consonantRel / NAME_VOWELS.size
        if (consonantIdx >= NAME_CONSONANTS.size) return null
        val vowelIdx = consonantRel % NAME_VOWELS.size
        return "${NAME_CONSONANTS[consonantIdx]}${NAME_VOWELS[vowelIdx]}$variant"
    }

    fun getSlicePolygonPts(sliceIdx: Int): List<DoubleArray> {
        val r0 = getRadial(sliceIdx)
        val r1 = getRadial(sliceIdx + 1)
        return listOf(
            r0[0].latlon,
            r0[lastIdx].latlon,
            r1[lastIdx].latlon,
            r1[0].latlon
        )
    }

    fun findSlice(latLng: LatLng): Slice {
        return findSliceInRange(latLng, 0, totalRadials)
    }

    fun findSliceInHour(latLng: LatLng, hour: Int): Slice {
        return findSliceInRange(latLng, hour * slicesPerHour, (hour + 1) * slicesPerHour)
    }

    fun findSliceInRange(latLng: LatLng, slice0: Int, slice1: Int): Slice {
        for (i in slice0 until slice1) {
            val slice = Slice(i, getRadial(i), getRadial(i + 1), divs)
            if (slice.containsLatLon(latLng)) return slice
        }
        return Slice()
    }

    fun getRingPolygon(index: Int): List<DoubleArray> {
        return radials.map { it[index].latlon }
    }

    /**
     * Circumferential polyline at the inner edge of block [blockIdx] —
     * the boundary that block sits just outside of — traced across every
     * radial. Block boundaries fall inside a section at an interpolated
     * position, so this is not one of the raw JSON ring boundaries.
     */
    fun getBlockBoundaryRing(blockIdx: Int): List<DoubleArray> {
        var cumulative = 0
        var section = 0
        var sectionIdx = blockIdx
        for (i in divs.indices) {
            if (blockIdx < cumulative + divs[i]) {
                section = i
                sectionIdx = blockIdx - cumulative
                break
            }
            cumulative += divs[i]
        }
        val xi = sectionIdx.toDouble() / divs[section]
        return radials.map { radial ->
            interpolateXi(xi, radial[section].latlon, radial[section + 1].latlon)
        }
    }

    fun getClockOutline(): List<List<DoubleArray>> {
        val pts = mutableListOf<List<DoubleArray>>()

        // Outer ring outline
        val outline = radials.map { it[0].latlon }.toMutableList()
        outline.add(radials[0][0].latlon) // close the loop
        pts.add(outline)

        // 12 hour radial lines
        for (i in 0 until 12) {
            val radial = getRadial(i * slicesPerHour)
            pts.add(listOf(radial[0].latlon, radial[lastIdx].latlon))
        }

        // Cross line (3 o'clock to 9 o'clock)
        val r3 = getRadial(3 * slicesPerHour)
        val r9 = getRadial(9 * slicesPerHour)
        pts.add(listOf(r3[lastIdx].latlon, r9[lastIdx].latlon))

        return pts
    }
}
